import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { SERVICES } from "../../data/services";
import { Reveal, SectionHeading } from "../site/Primitives";
import { useEnquiry } from "../site/EnquiryContext";

const PRINCIPLES = [
  { t: "Quality by Design", d: "A curated approach focused on professional experience, relevant expertise, and suitability for each engagement." },
  { t: "Built Around Your Needs", d: "Relevant experts selected according to service requirements, industry context, scope, and jurisdiction." },
  { t: "One Coordinated Experience", d: "A streamlined point of coordination for enquiries, engagement documentation, compliance steps, and billing arrangements, subject to the agreed engagement model." },
];

export const Positioning = () => (
  <section className="border-t border-slate-200 bg-white py-24 lg:py-32" data-testid="home-positioning">
    <div className="container-x grid gap-16 lg:grid-cols-12">
      <Reveal className="lg:col-span-5">
        <p className="eyebrow">Our position</p>
        <h2 className="h-section mt-5">The right expertise changes everything.</h2>
      </Reveal>
      <Reveal delay={0.1} className="lg:col-span-6 lg:col-start-7">
        <p className="lead">Finding the right specialist should not require navigating unnecessary layers of complexity. Trusted Leap is designed to connect organisations directly with experienced professionals whose expertise aligns with their needs, objectives, and operating environment.</p>
      </Reveal>
    </div>
    <div className="container-x mt-20 grid gap-px overflow-hidden rounded-lg border border-slate-200 bg-slate-200 md:grid-cols-3">
      {PRINCIPLES.map((p, i) => (
        <Reveal key={p.t} delay={i * 0.08} className="bg-white p-8 lg:p-10">
          <span className="font-display text-sm font-semibold text-[#2563EB]">0{i + 1}</span>
          <h3 className="mt-8 font-display text-xl font-semibold tracking-tight text-[#0B1220]">{p.t}</h3>
          <p className="mt-4 text-[15px] leading-relaxed text-slate-600">{p.d}</p>
        </Reveal>
      ))}
    </div>
  </section>
);

const ServiceCard = ({ s, i, featured }) => (
  <Reveal delay={i * 0.06}>
    <Link to={`/services/${s.slug}`} className={`group flex h-full flex-col rounded-lg border p-8 transition-[border-color,transform,box-shadow,background-color] duration-300 hover:-translate-y-1 lg:p-10 ${featured ? "border-[#0B1220] bg-[#0B1220] text-white hover:shadow-[0_30px_60px_-30px_rgba(11,18,32,0.6)]" : "border-slate-200 bg-white hover:border-slate-300 hover:shadow-[0_24px_50px_-30px_rgba(11,18,32,0.3)]"}`} data-testid={`home-service-card-${s.slug}`}>
      <div className="flex items-start justify-between">
        <s.icon className={`h-7 w-7 ${featured ? "text-blue-300" : "text-[#2563EB]"}`} strokeWidth={1.4} />
        <span className={`text-xs font-semibold tracking-[0.2em] ${featured ? "text-slate-400" : "text-slate-400"}`}>0{i + 1}</span>
      </div>
      <h3 className={`mt-10 font-display text-2xl font-semibold uppercase tracking-[0.04em] ${featured ? "text-white" : "text-[#0B1220]"}`}>{s.name}</h3>
      <p className={`mt-4 text-[15px] leading-relaxed ${featured ? "text-slate-300" : "text-slate-600"}`}>{s.summary}</p>
      <p className={`mt-7 text-[11px] font-semibold uppercase tracking-[0.18em] ${featured ? "text-slate-400" : "text-slate-500"}`}>Independent expertise across</p>
      <ul className="mt-3 space-y-2">
        {s.items.map((it) => (
          <li key={it} className={`flex gap-3 text-sm ${featured ? "text-slate-200" : "text-[#111827]"}`}>
            <span className={`mt-2 h-px w-3 shrink-0 ${featured ? "bg-blue-300" : "bg-[#2563EB]"}`} />{it}
          </li>
        ))}
      </ul>
      <span className={`mt-auto inline-flex items-center gap-2 pt-10 text-sm font-semibold ${featured ? "text-white" : "text-[#0B1220] group-hover:text-[#2563EB]"}`}>
        Explore Services <ArrowUpRight className="h-4 w-4 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
      </span>
    </Link>
  </Reveal>
);

export const ServiceLines = () => (
  <section className="bg-[#F4F6F8] py-24 lg:py-32" data-testid="home-services">
    <div className="container-x">
      <SectionHeading eyebrow="Service lines" title="Specialist expertise. Across five disciplines." intro="Categories of professional services available through the Trusted Leap network of independent practitioners. Availability depends on each professional's qualifications, authorisations and jurisdiction." />
      <div className="mt-16 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        {SERVICES.map((s, i) => <ServiceCard key={s.slug} s={s} i={i} featured={i === 0} />)}
      </div>
    </div>
  </section>
);

const STEPS = [
  { t: "Tell Us What You Need", d: "The client describes the challenge, required expertise, location, timeline, and expected outcomes." },
  { t: "Curated Expert Matching", d: "Trusted Leap reviews the requirements and identifies suitable professionals from its network." },
  { t: "Review Your Shortlist", d: "The client reviews relevant profiles, experience, capabilities, and engagement fit." },
  { t: "Engage with Confidence", d: "The parties move through the agreed scope, commercial terms, documentation, compliance checks, and engagement process." },
];

export const Process = () => {
  const { openEnquiry } = useEnquiry();
  return (
    <section className="bg-white py-24 lg:py-32" data-testid="home-process">
      <div className="container-x">
        <div className="flex flex-col justify-between gap-10 lg:flex-row lg:items-end">
          <SectionHeading eyebrow="How Trusted Leap works" title="From business challenge to the right expert." />
          <Link to="/how-it-works" className="link-arrow shrink-0" data-testid="home-process-learn-more">The full process <ArrowRight className="h-4 w-4" /></Link>
        </div>
        <ol className="relative mt-16 grid gap-0 lg:grid-cols-4">
          <span className="absolute left-[11px] top-3 h-[calc(100%-24px)] w-px bg-slate-200 lg:left-0 lg:top-[11px] lg:h-px lg:w-full" aria-hidden="true" />
          {STEPS.map((s, i) => (
            <Reveal as="li" key={s.t} delay={i * 0.1} className="relative pb-12 pl-12 lg:pb-0 lg:pl-0 lg:pr-10">
              <span className={`absolute left-0 top-0 flex h-6 w-6 items-center justify-center rounded-full border-2 bg-white lg:relative ${i === 3 ? "border-[#2563EB]" : "border-[#0B1220]"}`}>
                <span className={`h-2 w-2 rounded-full ${i === 3 ? "bg-[#2563EB]" : "bg-[#0B1220]"}`} />
              </span>
              <p className="mt-0 text-xs font-semibold uppercase tracking-[0.2em] text-slate-400 lg:mt-8">Step {i + 1}</p>
              <h3 className="mt-3 font-display text-xl font-semibold tracking-tight text-[#0B1220]">{s.t}</h3>
              <p className="mt-3 text-[15px] leading-relaxed text-slate-600">{s.d}</p>
            </Reveal>
          ))}
        </ol>
        <Reveal className="mt-20 grid items-center gap-8 rounded-lg border border-slate-200 bg-[#F4F6F8] p-8 lg:grid-cols-[1fr_auto] lg:p-12">
          <p className="max-w-3xl font-display text-lg font-medium leading-relaxed text-[#0B1220] md:text-xl" data-testid="home-72h-statement">
            <span className="text-[#2563EB]">Our objective:</span> a tailored shortlist within 72 hours of receiving a sufficiently detailed request, subject to the complexity and availability of the required expertise.
          </p>
          <button type="button" onClick={() => openEnquiry()} className="btn-primary" data-testid="home-start-request-button">Start Your Request <ArrowRight className="h-4 w-4" /></button>
        </Reveal>
      </div>
    </section>
  );
};

const DIFFS = [
  { t: "Curated, Not Crowded", d: "Prioritise relevant, experienced professionals over an overwhelming directory of profiles." },
  { t: "Direct Access to Experience", d: "Bring businesses closer to senior independent practitioners." },
  { t: "Cross-Jurisdictional Perspective", d: "Support requirements that span industries, markets, and jurisdictions, subject to professional availability and qualifications." },
  { t: "Streamlined Coordination", d: "Reduce administrative friction through a coordinated engagement process." },
  { t: "Designed for Trust", d: "Emphasise professional suitability, confidentiality, conflicts checks, transparent processes, and appropriate compliance safeguards." },
];

export const WhyUs = () => (
  <section className="border-t border-slate-200 bg-white py-24 lg:py-32" data-testid="home-why">
    <div className="container-x grid gap-16 lg:grid-cols-12">
      <div className="lg:col-span-5">
        <div className="lg:sticky lg:top-32">
          <SectionHeading eyebrow="Why Trusted Leap" title="A smarter way to access professional expertise." />
          <div className="relative mt-12 hidden aspect-[4/3] overflow-hidden rounded-lg lg:block">
            <img src="/images/collaboration.jpg" alt="Professionals collaborating around a meeting table" className="h-full w-full object-cover" loading="lazy" />
          </div>
        </div>
      </div>
      <ul className="lg:col-span-6 lg:col-start-7">
        {DIFFS.map((d, i) => (
          <Reveal as="li" key={d.t} delay={i * 0.05} className="grid grid-cols-[48px_1fr] gap-4 border-t border-slate-200 py-10 first:border-t-0 first:pt-0">
            <span className="font-display text-sm font-semibold text-[#2563EB]">0{i + 1}</span>
            <div>
              <h3 className="font-display text-2xl font-semibold tracking-tight text-[#0B1220]">{d.t}</h3>
              <p className="mt-3 text-[15px] leading-relaxed text-slate-600">{d.d}</p>
            </div>
          </Reveal>
        ))}
      </ul>
    </div>
  </section>
);

export const Audiences = () => {
  const { openEnquiry } = useEnquiry();
  return (
    <section className="bg-[#F4F6F8] py-24 lg:py-32" data-testid="home-audiences">
      <div className="container-x grid gap-6 lg:grid-cols-2">
        <Reveal className="flex flex-col rounded-lg border border-slate-200 bg-white p-10 lg:p-14">
          <p className="eyebrow">For businesses</p>
          <h2 className="mt-6 font-display text-3xl font-semibold leading-tight tracking-[-0.02em] text-[#0B1220] lg:text-4xl">The expertise you need, within reach.</h2>
          <p className="mt-6 max-w-md leading-relaxed text-slate-600">Tell us about your business challenge. We will work to identify professionals whose experience and capabilities align with your requirements.</p>
          <div className="mt-auto flex flex-wrap items-center gap-6 pt-12">
            <button type="button" onClick={() => openEnquiry()} className="btn-primary" data-testid="audience-find-expert-button">Find an Expert <ArrowRight className="h-4 w-4" /></button>
            <Link to="/for-businesses" className="link-arrow" data-testid="audience-businesses-link">For Businesses</Link>
          </div>
        </Reveal>
        <Reveal delay={0.1} className="relative flex flex-col overflow-hidden rounded-lg bg-[#111827] p-10 text-white lg:p-14">
          <img src="/images/handshake.jpg" alt="" className="absolute inset-0 h-full w-full object-cover opacity-20" loading="lazy" />
          <div className="relative flex h-full flex-col">
            <p className="eyebrow !text-blue-300">For independent professionals</p>
            <h2 className="mt-6 font-display text-3xl font-semibold leading-tight tracking-[-0.02em] lg:text-4xl">Your expertise deserves the right opportunities.</h2>
            <p className="mt-6 max-w-md leading-relaxed text-slate-300">Join a curated professional network designed to connect experienced practitioners with relevant, high-value business mandates.</p>
            <div className="mt-auto pt-12">
              <Link to="/for-experts#apply" className="btn-light" data-testid="audience-apply-link">Apply to Join the Network <ArrowRight className="h-4 w-4" /></Link>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
